import {
  ApplicationConfig,
  inject,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient } from '@angular/common/http';
import { provideRouter, Router } from '@angular/router';
import { provideAuth, withAuthInterceptor } from '@presencelearning/auth';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import {
  angularPlugin,
  provideDatadogErrorHandler,
  provideDatadogRouter,
} from '@datadog/browser-rum-angular';
import {
  DEFAULT_COMMAND_PALETTE_PLACEHOLDER,
  DEFAULT_COMMAND_PALETTE_SHORTCUT,
  PL_COMMAND_PALETTE_CONFIG,
} from '@presencelearning/ui/command-palette';
import { providePlIcons } from '@presencelearning/icons';
import { detectBrowserLocale, providePlI18n } from '@presencelearning/i18n';
import { TranslocoService } from '@jsverse/transloco';
import { provideRemoteConfig, withConfigCat } from '@presencelearning/remote-config';
import { backendSecureRoutes } from './backend-secure-routes';
import { routes } from './app.routes';
import { createPagesSearchSource } from './pages-search-source';
import { environment } from '../environments/environment';
import en from '../assets/i18n/en.json';
import es from '../assets/i18n/es.json';

const AVAILABLE_LANGS = ['en', 'es'];

// Guarded so this is a genuine no-op (not even the SDKs' internal state gets
// touched) in local dev, where environment.ts intentionally leaves
// datadogApplicationId/datadogClientToken empty - staging and production
// have real values (see environment.staging.ts / environment.production.ts).
function initializeDatadog(): void {
  if (environment.datadogApplicationId && environment.datadogClientToken) {
    datadogRum.init({
      applicationId: environment.datadogApplicationId,
      clientToken: environment.datadogClientToken,
      site: 'datadoghq.com',
      service: 'future-room',
      env: environment.env,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      plugins: [angularPlugin({ router: true })],
    });

    datadogLogs.init({
      clientToken: environment.datadogClientToken,
      site: 'datadoghq.com',
      service: 'future-room',
      env: environment.env,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
    });
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideDatadogRouter(),
    provideDatadogErrorHandler(),
    provideAnimations(),
    provideHttpClient(withAuthInterceptor()),
    // secureRoutes defaults to [] (deny-by-default). Without this allowlist the
    // appointments call leaves with no Authorization header and api-workplace
    // answers 401. The door origin is included so opening a room sends the same
    // token; the door rejects a leader launch that does not carry it.
    provideAuth({
      issuerUrl: environment.auth.issuerUrl,
      clientId: environment.auth.clientId,
      secureRoutes: backendSecureRoutes(environment.apps, environment.lessonspaceDoorUrl),
    }),
    providePlIcons(),
    providePlI18n({
      availableLangs: AVAILABLE_LANGS,
      defaultLang: detectBrowserLocale(AVAILABLE_LANGS, 'en'),
      translations: { en, es },
    }),
    provideRemoteConfig(withConfigCat({ sdkKey: environment.configCatKey })),
    // Runs only when Angular actually bootstraps the application, not merely
    // when this module is imported (e.g. by a future spec or Storybook config
    // that imports appConfig without booting the real app).
    provideEnvironmentInitializer(initializeDatadog),
    // provideCommandPalette() takes sources as a plain array, resolved before
    // any injector exists - it can't inject Router itself. Providing the
    // token directly via useFactory (mirroring @presencelearning/ai's
    // PRESENCE_AI_CONFIG pattern) runs inside an injection context instead.
    {
      provide: PL_COMMAND_PALETTE_CONFIG,
      useFactory: () => ({
        sources: [createPagesSearchSource(inject(Router), inject(TranslocoService))],
        debounceMs: 300,
        minSearchLength: 2,
        shortcut: DEFAULT_COMMAND_PALETTE_SHORTCUT,
        placeholder: DEFAULT_COMMAND_PALETTE_PLACEHOLDER,
      }),
    },
  ],
};
